import {AfterViewInit, ChangeDetectorRef, Component, ElementRef, Inject, Input, OnInit, PLATFORM_ID, ViewChild} from '@angular/core';
import {CountrySwitchModalComponent} from '@app/layout/header/country-selector/country-switch-modal/country-switch-modal.component';
import {isPlatformBrowser} from '@angular/common';
import {share, take} from 'rxjs/operators';
import {BehaviorSubject, Observable} from 'rxjs';
import {Address, Country, Product, Region, SiteConnector} from '@spartacus/core';
import {GoogleMapsService} from '../../../services/google-maps.service';
import {LeadFormContractDataModel, LeadFormCreatorModel, LeadFormModel, LeadFormRegionModel} from '../lead-form.model';
import {CountrySelectorComponent} from 'ng-design-system';
import {CountrySelectorService, CountrySelectorStatus} from '@app/services/country-selector.service';
import {ControlContainer, NgForm} from '@angular/forms';
import {ValidationService} from '@dcp/configurator';
import {UserType} from '@app/product/models/dcp-user-type.model';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';

@UntilDestroy()
@Component({
  selector: 'dcp-lead-form-company-details',
  templateUrl: './lead-form-company-details.component.html',
  styleUrls: ['./lead-form-company-details.component.scss'],
  viewProviders: [{provide: ControlContainer, useExisting: NgForm}]
})
export class LeadFormCompanyDetailsComponent implements OnInit, AfterViewInit {

  /** keeps track of the previously picked country */
  public lastCountryIsocode: string;
  public initialCountry: Country;
  public countries$: Observable<Country[]>;
  public regions$: Observable<Region[]>;
  @Input() leadFormModel: LeadFormModel;
  @Input() leadFormContractDataModel: LeadFormContractDataModel;
  @Input() product: Product;
  @Input() userType: UserType;
  @Input() sapEmployeeSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  @Input() showContractDetails: Boolean = false;
  /** tracks if the onSubmit function has been called */
  @Input() submitted = false;
  @ViewChild('countrySwitchModalComponent', {static: false}) countrySwitchModalComponentRef: CountrySwitchModalComponent;
  @ViewChild('companyName', {read: ElementRef, static: false}) companyNameRef: ElementRef;
  @ViewChild('streetAddress', {read: ElementRef, static: false}) streetAddressRef: ElementRef;
  @ViewChild('countrySelector', {
    read: CountrySelectorComponent,
    static: false
  }) countrySelector: CountrySelectorComponent;

  constructor(
    @Inject(PLATFORM_ID) private platformId: object,
    public cdRef: ChangeDetectorRef,
    public countrySelectorService: CountrySelectorService,
    public googleMapsService: GoogleMapsService,
    private siteConnector: SiteConnector,
    private validationService: ValidationService,
  ) {
  }

  ngOnInit() {
    const headerCountry: Country = this.countrySelectorService.getCurrentCountry();
    this.setFormCountry(headerCountry);
    this.countries$ = this.siteConnector.getCountries().pipe(share());
  }

  ngAfterViewInit(): void {
    this.attachGoogleMapsAutocomplete();
  }

  /**
   * Attaches Google Maps Autocomplete functionality to the Company Name and Street address input fields.
   */
  attachGoogleMapsAutocomplete() {
    this.setRegionsObservable();
    this.googleMapsService.attachGoogleMapsAutocomplete(
      this.companyNameRef, this.streetAddressRef, this.regions$, this.leadFormModel
    );
    this.cdRef.detectChanges();
  }

  /**
   * Country change handler
   */
  onCountryChange(event: string): void {
    const currentCountryIsocode: string = event;
    if (currentCountryIsocode !== this.lastCountryIsocode) {
      this.countrySwitchModalComponentRef.confirmCountryChange(currentCountryIsocode, this.product)
        .then((status) => {
          this.handleCountryConfirmation(status, currentCountryIsocode);
        });
    }
  }

  /**
   * Handles the return value of th countrySwitchModalComponentRef.confirmCountryChange method.
   *
   * @param status string returned by confirmCountryChange method.
   * @param currentCountryIsocode country to switch to.
   */
  handleCountryConfirmation(status: string, currentCountryIsocode: string) {
    if (status === CountrySelectorStatus.PRODUCT_ALLOWED) {
      this.lastCountryIsocode = currentCountryIsocode;
      this.setRegionsObservable();
      if (isPlatformBrowser(this.platformId)) {
        this.regions$.pipe(take(1), untilDestroyed(this)).subscribe((regions) => {
          this.googleMapsService.newAutocompleteCountry(this.leadFormModel, regions);
        });
      }
      this.unsetAddressFields();
    } else if (status === CountrySelectorStatus.WITHDRAW_COUNTRY_SELECTOR) {
      this.leadFormModel.addressesMap.LTAC.country.isocode = this.lastCountryIsocode;
      this.cdRef.detectChanges();
    }
    if (this.countrySelector) {
      this.countrySelector.focus();
    }
  }

  /**
   * Street Address focus handler
   *
   * @param event set or remove focus
   */
  onStreetAddressFocus(event: boolean): void {
    if (!event && !this.streetAddressRef.nativeElement.innerValue && !this.leadFormModel.addressesMap.LTAC.line1) {
      this.unsetAddressFields();
    }
  }


  /**
   * Region change handler
   *
   * @param regionIsoCodeArr region isocode array
   */
  onRegionChange(regionIsoCodeArr: string[]): void {
    const region: LeadFormRegionModel = new LeadFormRegionModel();
    region.isocode = regionIsoCodeArr[0];
    this.leadFormModel.addressesMap.LTAC.regionIsocodeArray = regionIsoCodeArr;
    if (region.isocode) {
      this.leadFormModel.addressesMap.LTAC.region = region;
    }
    this.cdRef.detectChanges();
  }

  /**
   * Unset toggleable address fields
   */
  unsetAddressFields(): void {
    this.leadFormModel.addressesMap.LTAC.line1 = undefined;
    this.leadFormModel.addressesMap.LTAC.town = undefined;
    this.leadFormModel.addressesMap.LTAC.postalCode = undefined;
    this.leadFormModel.addressesMap.LTAC.region = undefined;
    this.leadFormModel.addressesMap.LTAC.regionIsocodeArray = undefined;
    this.cdRef.detectChanges();
  }

  /**
   * Fills defaultUnitAddress into the form if the user is logged in.
   *
   * @param user Form the user service.
   */
  prefillDefaultUnitAddress(user: LeadFormCreatorModel) {
    const address: Address = user.defaultUnitAddress;
    if (address) {
      this.setFormCountry(address.country);
      this.leadFormModel.addressesMap.LTAC.companyName = address.companyName;
      this.leadFormModel.addressesMap.LTAC.line1 = address.line1;
      this.leadFormModel.addressesMap.LTAC.town = address.town;
      this.leadFormModel.addressesMap.LTAC.postalCode = address.postalCode;
      this.leadFormModel.addressesMap.LTAC.region = address.region;
      if (address.region) {
        this.leadFormModel.addressesMap.LTAC.regionIsocodeArray = [address.region.isocode];
      }
      this.cdRef.detectChanges();
    }
  }

  /**
   * Checks if a validation error exists for the validator name.
   *
   * @param dsInput InputComponent to check for errors.
   * @param validatorName error to look after.
   * @return true if showError is true and the errors array has a value that equals the validatorName.
   */
  hasValidationFailed(formControl: { errors: object }, validatorName: string): boolean {
    return this.validationService.hasValidationFailed(formControl, validatorName, this.submitted);
  }

  /**
   * Sets the country per selected in the lead form country selector.
   *
   * @param country Country to set as per selected country
   */
  setFormCountry(country: Country) {
    this.leadFormModel.addressesMap.LTAC.country.isocode = country.isocode;
    this.initialCountry = country;
    this.lastCountryIsocode = country.isocode;
    this.cdRef.detectChanges();
  }

  setRegionsObservable(): void {
    this.regions$ = this.siteConnector.getRegions(this.leadFormModel.addressesMap.LTAC.country.isocode).pipe(share());
  }

  /**
   * In case an external customer ID is still store in the user session and the login
   * is being done by an employee - we show corresponding field on the lead form.
   */
  shouldShowExternalHarmonyCustomerId(): boolean {
    return this.showContractDetails && this.sapEmployeeSubject.value;
  }
}
